import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DownOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { FiRefreshCw } from 'react-icons/fi';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import AppRangePicker from '@/components/common/AppRangePicker';
import PermissionGate from '../../../../components/auth/PermissionGate';
import basketOrderService from '../../../../services/basketOrderService';
import orderService from '../../../../services/orderService';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import {
  BASKET_STATUS_FILTER_OPTIONS,
  BASKET_STATUS_LABEL,
  resolveBasketStatusColor,
  resolveBasketStatusLabel,
} from './basketOrderStatus';
import BasketOrderExpandedRow from './BasketOrderExpandedRow';
import {
  getInvestorProfilePath,
  getPartnerDetailsPath,
  ProfileLink,
  resolveInvestorUuid,
  resolvePartnerUuid,
} from '../order-management/orderEntityLinks';
import { canTransferBasketOrder, getPublicId } from './basketOrder.helpers';
import {
  STATUS_COLOR,
  STATUS_LABEL,
} from '../order-management/orderStatus';
import AltcasePriceCell from '../order-management/AltcasePriceCell';


const formatDateTime = (value) =>
  value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—';

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
};

const getDefaultCreatedRange = () => {
  const today = dayjs();
  const fyStartYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return [today.year(fyStartYear).month(3).date(1).startOf('day'), today];
};

const isDefaultCreatedRange = (range) => {
  if (!range?.[0] || !range?.[1]) return range == null;
  const [defaultStart, defaultEnd] = getDefaultCreatedRange();
  return range[0].isSame(defaultStart, 'day') && range[1].isSame(defaultEnd, 'day');
};

export default function BasketOrderManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.ORDERS_MANAGE);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // ── Filter / pagination — seeded from URL on first render ──────────
  const [page, setPage] = useState(() => {
    const v = Number(searchParams.get('page'));
    return v > 0 ? v : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(searchParams.get('limit'));
    return v > 0 ? v : 10;
  });
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || null);
  const [createdRange, setCreatedRange] = useState(() => {
    const f = searchParams.get('date_from');
    const t = searchParams.get('date_to');
    return f && t ? [dayjs(f), dayjs(t)] : getDefaultCreatedRange();
  });
  const [transferRange, setTransferRange] = useState(() => {
    const f = searchParams.get('tf_from');
    const t = searchParams.get('tf_to');
    return f && t ? [dayjs(f), dayjs(t)] : null;
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);

  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);
  const [lineDrawerLoading, setLineDrawerLoading] = useState(false);
  const [lineOrder, setLineOrder] = useState(null);

  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [utrOpen, setUtrOpen] = useState(false);
  const [confirmForm] = Form.useForm();
  const [utrForm] = Form.useForm();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm] = Form.useForm();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await basketOrderService.adminListBasketOrders({
        page,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
        date_from: createdRange?.[0]?.startOf('day').toISOString(),
        date_to: createdRange?.[1]?.endOf('day').toISOString(),
        transfer_date_from: transferRange?.[0]?.startOf('day').toISOString(),
        transfer_date_to: transferRange?.[1]?.endOf('day').toISOString(),
      });
      setOrders(result?.basket_orders ?? []);
      setTotal(result?.pagination?.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load basket orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, createdRange, transferRange]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // ── Sync all filter / pagination state → URL (replace, no history spam) ──
  useEffect(() => {
    const p = {};
    if (search)             p.q         = search;
    if (statusFilter)       p.status    = statusFilter;
    if (createdRange?.[0])  p.date_from = createdRange[0].format('YYYY-MM-DD');
    if (createdRange?.[1])  p.date_to   = createdRange[1].format('YYYY-MM-DD');
    if (transferRange?.[0]) p.tf_from   = transferRange[0].format('YYYY-MM-DD');
    if (transferRange?.[1]) p.tf_to     = transferRange[1].format('YYYY-MM-DD');
    if (page > 1)           p.page      = page;
    if (pageSize !== 10)    p.limit     = pageSize;
    setSearchParams(p, { replace: true });
  }, [search, statusFilter, createdRange, transferRange, page, pageSize, setSearchParams]);

  const handleReset = () => {
    setSearch('');
    setStatusFilter(null);
    setCreatedRange(getDefaultCreatedRange());
    setTransferRange(null);
    setPage(1);
    setPageSize(10);
  };

  const hasActiveFilters = Boolean(
    search ||
      statusFilter ||
      (createdRange && !isDefaultCreatedRange(createdRange)) ||
      transferRange
  );

  // Count only drawer-managed filters so the badge reflects hidden filter state.
  const filterDrawerCount = [
    !!statusFilter,
    !!(createdRange && !isDefaultCreatedRange(createdRange)),
    !!transferRange,
  ].filter(Boolean).length;

  const handleLineUpdated = useCallback(async () => {
    await loadOrders();
    if (drawerOpen && drawerOrder) {
      try {
        const data = await basketOrderService.adminGetBasketOrder(getPublicId(drawerOrder));
        setDrawerOrder(data);
      } catch {
        // list already refreshed
      }
    }
    if (lineDrawerOpen && lineOrder) {
      const lineId = getPublicId(lineOrder);
      if (lineId) {
        try {
          const res = await orderService.adminGetOrderDetail(lineId);
          const order = res?.order ?? res;
          setLineOrder({
            ...order,
            transfer_before_payment:
              order.transfer_before_payment ?? lineOrder.transfer_before_payment,
          });
        } catch {
          // list already refreshed
        }
      }
    }
  }, [loadOrders, drawerOpen, drawerOrder, lineDrawerOpen, lineOrder]);

  const openDetailDrawer = async (row) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerOrder(null);
    try {
      const data = await basketOrderService.adminGetBasketOrder(getPublicId(row));
      setDrawerOrder(data);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load basket order');
      setDrawerOpen(false);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openLineDrawer = async (lineRow, parentBasket = null) => {
    const lineId = getPublicId(lineRow);
    if (!lineId) return;
    setLineDrawerOpen(true);
    setLineDrawerLoading(true);
    setLineOrder(null);
    try {
      const res = await orderService.adminGetOrderDetail(lineId);
      const order = res?.order ?? res;
      setLineOrder({
        ...order,
        transfer_before_payment:
          order.transfer_before_payment ?? parentBasket?.transfer_before_payment,
      });
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load line order');
      setLineDrawerOpen(false);
    } finally {
      setLineDrawerLoading(false);
    }
  };

  const runConfirmPayment = async () => {
    const orderId = getPublicId(actionTarget);
    if (!orderId) return;
    try {
      const values = await confirmForm.validateFields();
      setActionSubmitting(true);
      await basketOrderService.confirmPayment(orderId, {
        utr_number: values.utr_number.trim(),
      });
      message.success('Basket payment confirmed');
      setConfirmOpen(false);
      confirmForm.resetFields();
      setActionTarget(null);
      await loadOrders();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Confirm failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runUpdateUtr = async () => {
    const orderId = getPublicId(actionTarget);
    if (!orderId) return;
    try {
      const values = await utrForm.validateFields();
      setActionSubmitting(true);
      await basketOrderService.updateUtr(orderId, values.utr_number.trim());
      message.success('UTR updated');
      setUtrOpen(false);
      utrForm.resetFields();
      setActionTarget(null);
      await loadOrders();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runSendPaymentLink = async (row) => {
    try {
      setActionSubmitting(true);
      await basketOrderService.sendPaymentLink(getPublicId(row), {
        send_to_investor: true,
        send_to_partner: true,
      });
      message.success('Payment link sent');
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Send failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runSyncStatus = async (row) => {
    try {
      setActionSubmitting(true);
      const result = await basketOrderService.syncStatus(getPublicId(row));
      if (result?.synced) {
        message.success('Basket order marked completed');
      } else {
        message.info('Not all line orders are transferred yet');
      }
      await loadOrders();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Sync failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const openBasketTransfer = (row) => {
    setActionTarget(row);
    transferForm.setFieldsValue({ transfer_date: dayjs(), remarks: '' });
    setTransferOpen(true);
  };

  const runBasketTransfer = async () => {
    const basketId = getPublicId(actionTarget);
    if (!basketId) return;
    try {
      const values = await transferForm.validateFields();
      setActionSubmitting(true);
      const result = await basketOrderService.transferShares(basketId, {
        transfer_date: values.transfer_date.startOf('day').toISOString(),
        remarks: values.remarks?.trim() || undefined,
      });
      const count = result?.transferred_count ?? 0;
      message.success(
        result?.synced
          ? `Transferred ${count} line(s); basket order completed`
          : `Transferred ${count} line(s)`
      );
      setTransferOpen(false);
      transferForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      if (drawerOpen && drawerOrder && getPublicId(drawerOrder) === basketId) {
        const data = await basketOrderService.adminGetBasketOrder(basketId);
        setDrawerOrder(data);
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Transfer failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const getRowActions = (row) => {
    const items = [{ key: 'view', label: 'View details', onClick: () => openDetailDrawer(row) }];

    if (!canManage) return items;

    if (row.status === 'ORDER_PLACED') {
      items.push({
        key: 'confirm',
        label: 'Confirm payment',
        onClick: () => {
          setActionTarget(row);
          confirmForm.setFieldsValue({ utr_number: row.utr_number || '' });
          setConfirmOpen(true);
        },
      });
      items.push({
        key: 'payment-link',
        label: 'Send payment link',
        onClick: () => runSendPaymentLink(row),
      });
    }

    if (row.status === 'PAYMENT_RECEIVED') {
      items.push({
        key: 'utr',
        label: 'Update UTR',
        onClick: () => {
          setActionTarget(row);
          utrForm.setFieldsValue({ utr_number: row.utr_number || '' });
          setUtrOpen(true);
        },
      });
      items.push({
        key: 'sync',
        label: 'Sync completion status',
        onClick: () => runSyncStatus(row),
      });
    }

    if (canTransferBasketOrder(row, row.items)) {
      items.push({
        key: 'transfer',
        label: 'Transfer shares (all lines)',
        onClick: () => openBasketTransfer(row),
      });
    }

    return items;
  };

  const getRowActionsRef = useRef(getRowActions);
  getRowActionsRef.current = getRowActions;

  const columns = useMemo(
    () => [
    {
      title: 'Basket',
      dataIndex: 'basket_name',
      key: 'basket_name',
      width: 160,
    },
    {
      title: 'Order #',
      dataIndex: 'basket_order_number',
      key: 'basket_order_number',
      width: 220,
    },
    {
      title: 'Investor',
      key: 'investor',
      width: 160,
      render: (_, row) => {
        const investorUuid = resolveInvestorUuid(row);
        const partnerUuid = resolvePartnerUuid(row);
        const profilePath = getInvestorProfilePath(investorUuid, partnerUuid);
        const name = row.investor?.name ?? row.investor_name ?? '—';
        return (
          <ProfileLink to={profilePath} className="whitespace-nowrap text-sm font-medium">
            {name}
          </ProfileLink>
        );
      },
    },
    {
      title: 'Partner',
      key: 'partner',
      width: 140,
      render: (_, row) => {
        const partnerPath = getPartnerDetailsPath(resolvePartnerUuid(row));
        const name = row.partner?.name ?? row.partner_name ?? '—';
        return (
          <ProfileLink to={partnerPath} className="whitespace-nowrap text-sm font-medium">
            {name}
          </ProfileLink>
        );
      },
    },
    {
      title: 'Total payable',
      dataIndex: 'total_payable',
      key: 'total_payable',
      width: 130,
      render: formatCurrency,
    },
    {
      title: 'UTR',
      dataIndex: 'utr_number',
      key: 'utr_number',
      width: 140,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: formatDateTime,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (_, row) => (
        <Tag color={resolveBasketStatusColor(row)}>
          {resolveBasketStatusLabel(row)}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 64,
      render: (_, row) => (
        <Dropdown menu={{ items: getRowActionsRef.current(row) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} aria-label="Actions" />
        </Dropdown>
      ),
    },
  ],
    [canManage],
  );

  const handlePaginationChange = useCallback((p, ps) => {
    setPage(p);
    setPageSize(ps);
  }, []);

  return (
    <DashboardPageWrapper>
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-sm">

        {/* ─── Sticky toolbar ─── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <PermissionGate permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
            <Tooltip title="Place basket order">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/pre-ipo/basket-order-management/place')}
                className="aspect-square px-0 sm:aspect-auto sm:px-4"
                aria-label="Place basket order"
              >
                <span className="hidden sm:inline">Place basket order</span>
              </Button>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
            <div className="mx-1 h-5 w-px shrink-0 bg-gray-200" />
          </PermissionGate>

          <Input
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search order #, investor, basket, UTR"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setPage(1)}
            className="min-w-0 flex-1"
          />

          <Badge count={filterDrawerCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
              className={`aspect-square px-0 sm:aspect-auto sm:px-4 ${filterDrawerCount > 0 ? 'border-blue-400 text-blue-600' : ''}`}
              aria-label="Filters"
            >
              <span className="hidden sm:inline">Filters</span>
            </Button>
          </Badge>

          <span className="hidden sm:inline-flex">
            <Tooltip title="Refresh">
              <Button
                icon={<FiRefreshCw />}
                onClick={loadOrders}
                loading={loading}
                aria-label="Refresh"
                className="aspect-square min-w-9 px-0"
              />
            </Tooltip>
          </span>
        </div>

        {/* ─── Active filter chips ─── */}
        {hasActiveFilters ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-100 bg-gray-50/60 px-4 py-2">
            {search ? (
              <Tag
                closable
                onClose={() => { setSearch(''); setPage(1); }}
                className="m-0 text-xs"
              >
                Search: <span className="font-medium">{search}</span>
              </Tag>
            ) : null}
            {statusFilter ? (
              <Tag
                closable
                onClose={() => { setStatusFilter(null); setPage(1); }}
                className="m-0 text-xs"
              >
                Status: <span className="font-medium">{BASKET_STATUS_LABEL[statusFilter] || statusFilter}</span>
              </Tag>
            ) : null}
            {createdRange && !isDefaultCreatedRange(createdRange) ? (
              <Tag
                closable
                onClose={() => { setCreatedRange(null); setPage(1); }}
                className="m-0 text-xs"
              >
                Created: <span className="font-medium">{createdRange[0].format('DD MMM')} – {createdRange[1].format('DD MMM')}</span>
              </Tag>
            ) : null}
            {transferRange ? (
              <Tag
                closable
                onClose={() => { setTransferRange(null); setPage(1); }}
                className="m-0 text-xs"
              >
                Transfer: <span className="font-medium">{transferRange[0].format('DD MMM')} – {transferRange[1].format('DD MMM')}</span>
              </Tag>
            ) : null}
            <button
              type="button"
              onClick={handleReset}
              className="ml-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-700"
            >
              Reset all
            </button>
          </div>
        ) : null}

        {/* ─── Table ─── */}
        <div className="min-h-0 flex-1 overflow-auto">
          <Table
            rowKey={(row) => getPublicId(row) || row.basket_order_number}
            columns={columns}
            dataSource={orders}
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            sticky
            className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
            expandable={{
              expandedRowRender: (record) => (
                <BasketOrderExpandedRow
                  basketRow={record}
                  items={record.items}
                  canManage={canManage}
                  onManageLine={openLineDrawer}
                  onLineUpdated={handleLineUpdated}
                />
              ),
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <DownOutlined
                    className="text-gray-500 transition-transform"
                    onClick={(e) => onExpand(record, e)}
                  />
                ) : (
                  <RightOutlined
                    className="text-gray-400 transition-transform"
                    onClick={(e) => onExpand(record, e)}
                  />
                ),
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: handlePaginationChange,
            }}
          />
        </div>
      </div>

      {/* ─── Filter Drawer ─── */}
      <Drawer
        title="Filters"
        placement="right"
        width={360}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => {
                handleReset();
                setFilterDrawerOpen(false);
              }}
            >
              Reset
            </Button>
            <Button type="primary" onClick={() => setFilterDrawerOpen(false)}>
              Apply
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="min-w-0">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Status</label>
            <Select
              allowClear
              placeholder="All statuses"
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v || null); setPage(1); }}
              options={BASKET_STATUS_FILTER_OPTIONS}
              className="w-full"
            />
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Created date</label>
            <AppRangePicker
              value={createdRange}
              onChange={(v) => { setCreatedRange(v || null); setPage(1); }}
              format="DD MMM YYYY"
              placeholder={['Start', 'End']}
              className="w-full"
            />
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Transfer date</label>
            <AppRangePicker
              value={transferRange}
              onChange={(v) => { setTransferRange(v || null); setPage(1); }}
              format="DD MMM YYYY"
              placeholder={['Start', 'End']}
              className="w-full"
            />
          </div>
        </div>
      </Drawer>

      <Modal
        title="Confirm basket payment"
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setActionTarget(null);
          confirmForm.resetFields();
        }}
        onOk={runConfirmPayment}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <Form form={confirmForm} layout="vertical">
          <Form.Item
            name="utr_number"
            label="UTR"
            rules={[{ required: true, message: 'UTR is required' }]}
          >
            <Input placeholder="UTR" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update UTR"
        open={utrOpen}
        onCancel={() => {
          setUtrOpen(false);
          setActionTarget(null);
          utrForm.resetFields();
        }}
        onOk={runUpdateUtr}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <Form form={utrForm} layout="vertical">
          <Form.Item
            name="utr_number"
            label="UTR"
            rules={[{ required: true, message: 'UTR is required' }]}
          >
            <Input placeholder="UTR" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Basket order details"
        width={720}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {drawerLoading ? (
          <Spin />
        ) : drawerOrder ? (
          <>
            <Descriptions column={1} size="small" bordered className="mb-4">
              <Descriptions.Item label="Order #">{drawerOrder.basket_order_number}</Descriptions.Item>
              <Descriptions.Item label="Basket">{drawerOrder.basket_name}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={resolveBasketStatusColor(drawerOrder)}>
                  {resolveBasketStatusLabel(drawerOrder)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Investor">{drawerOrder.investor?.name}</Descriptions.Item>
              <Descriptions.Item label="Partner">{drawerOrder.partner?.name}</Descriptions.Item>
              <Descriptions.Item label="Total payable">
                {formatCurrency(drawerOrder.total_payable)}
              </Descriptions.Item>
              <Descriptions.Item label="UTR">{drawerOrder.utr_number}</Descriptions.Item>
              <Descriptions.Item label="Created">{formatDateTime(drawerOrder.created_at)}</Descriptions.Item>
            </Descriptions>
            {canManage && canTransferBasketOrder(drawerOrder, drawerOrder.items) ? (
              <Button
                type="primary"
                className="mb-4"
                onClick={() => openBasketTransfer(drawerOrder)}
              >
                Transfer shares (all lines)
              </Button>
            ) : null}
            <BasketOrderExpandedRow
              basketRow={drawerOrder}
              items={drawerOrder.items}
              canManage={canManage}
              onManageLine={openLineDrawer}
              onLineUpdated={handleLineUpdated}
            />
          </>
        ) : null}
      </Drawer>

      <Drawer
        title="Line order"
        width={640}
        open={lineDrawerOpen}
        onClose={() => setLineDrawerOpen(false)}
      >
        {lineDrawerLoading ? (
          <Spin />
        ) : lineOrder ? (
          <>
            <Descriptions column={1} size="small" bordered className="mb-4">
              <Descriptions.Item label="Order #">{lineOrder.order_number}</Descriptions.Item>
              <Descriptions.Item label="Product">{lineOrder.product_name}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[lineOrder.status]}>
                  {STATUS_LABEL[lineOrder.status] || lineOrder.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Altcase price">
                <AltcasePriceCell
                  row={lineOrder}
                  canManage={canManage}
                  onAssigned={handleLineUpdated}
                />
              </Descriptions.Item>
            </Descriptions>
            <p className="text-sm text-gray-500">
              Assign Altcase price per line above, then use{' '}
              <strong>Transfer shares (all lines)</strong> on the basket order.
            </p>
          </>
        ) : null}
      </Drawer>

      <Modal
        title="Transfer basket shares"
        open={transferOpen}
        onCancel={() => {
          setTransferOpen(false);
          setActionTarget(null);
          transferForm.resetFields();
        }}
        onOk={runBasketTransfer}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <Form form={transferForm} layout="vertical" initialValues={{ transfer_date: dayjs() }}>
          <Form.Item
            name="transfer_date"
            label="Transfer date"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </DashboardPageWrapper>
  );
}
